/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import * as AppStrings from '@touchplan/strings';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { Attachment } from '../../../../app-data/models/Attachment';
import { analyticsEventsService } from '../../../../plain-javascript/services/analytics/AnalyticsEventsService';
import { bytesToMB } from '../../../../utils/file-utils';
import { AttachedFilesService } from '../../attached-files.service';
import { AttachmentsViewState } from '../../attachments-view-state';
import { IFileSelection } from '../attached-file-viewer/attached-file-viewer.component';

@Component({
	selector: 'image-list-view',
	templateUrl: './image-list-view.component.html',
	styleUrls: ['./image-list-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageListViewComponent {
	@Input() public acceptedFileExtensions: string[];
	@Input() public fileSizeLimit: number; // in MB

	public attachmentsViewState = AttachmentsViewState;

	constructor(
		public attachedFilesService: AttachedFilesService,
		private readonly bannerMessageHandler: BannerMessageHandler
	) {}

	public attachFiles(files: File[]): void {
		// future hook... could be used for dialogs etc
		let someFilesAreTooBig = false;
		let someFilesHaveBadExtensions = false;
		for (const file of files) {
			const fileSize = bytesToMB(file.size);
			// only upload files that are under file size limit, and which have a valid extension:
			const sizeOK = fileSize < this.fileSizeLimit;
			const extOK = this.attachedFilesService.extensionIsOK(file, this.acceptedFileExtensions);
			if (sizeOK && extOK) {
				this.attachedFilesService.api.addAttachment(file);
			}
			someFilesAreTooBig ||= !sizeOK;
			someFilesHaveBadExtensions ||= !extOK;
		}
		if (someFilesHaveBadExtensions) {
			const badExtMsg = AppStrings.TICKET_EDIT_CAN_ONLY_ATTACH_FILES_OF_SUPPORTED_TYPES(
				this.acceptedFileExtensions.join(', ')
			);
			if (someFilesAreTooBig) {
				// Display BOTH messages in one snackbar:
				this.bannerMessageHandler.displayWarningMessage(
					`${badExtMsg}.  ${AppStrings.TICKET_EDIT_CAN_ONLY_ATTACH_FILES_UP_TO_X_MB(this.fileSizeLimit)}`
				);
			} else {
				this.bannerMessageHandler.displayWarningMessage(badExtMsg);
			}
		} else if (someFilesAreTooBig) {
			this.bannerMessageHandler.displayWarningMessage(
				AppStrings.TICKET_EDIT_CAN_ONLY_ATTACH_FILES_UP_TO_X_MB(this.fileSizeLimit)
			);
		}
	}

	public fileClickHandler(file: Attachment): void {
		this.attachedFilesService.selectFile(file);
		this.attachedFilesService.changeViewState(AttachmentsViewState.FileCarouselView);
		analyticsEventsService.fileViewed(this.attachedFilesService.planId, file.view.type);
	}

	public fileSelectedHandler(fileSelection: IFileSelection): void {
		if (fileSelection.selected) {
			this.attachedFilesService.addToFileSelection(fileSelection.file);
		} else {
			this.attachedFilesService.removeFromFileSelection(fileSelection.file);
		}
	}
}
